import type { PropsWithChildren } from 'react'
import { createContext, useCallback, useMemo, useRef, useState } from 'react'

import {
  type OrderDraftItemMutationStatusEnum,
  OrderDraftItemMutationStatuses,
} from './OrderDraftItemMutationStatusEnum'

export type OrderDraftItemMutationVisibleStatus = Exclude<
  OrderDraftItemMutationStatusEnum,
  typeof OrderDraftItemMutationStatuses.COMPLETED
>

type InProgressStatus =
  | typeof OrderDraftItemMutationStatuses.CREATING
  | typeof OrderDraftItemMutationStatuses.UPDATING
  | typeof OrderDraftItemMutationStatuses.DELETING

type TerminalStatus =
  | typeof OrderDraftItemMutationStatuses.COMPLETED
  | typeof OrderDraftItemMutationStatuses.FAILED_TO_CREATE
  | typeof OrderDraftItemMutationStatuses.FAILED_TO_UPDATE
  | typeof OrderDraftItemMutationStatuses.FAILED_TO_DELETE

interface OrderDraftItemMutationEntry {
  status: OrderDraftItemMutationVisibleStatus
  latestMutationSeq: number
}

type OrderDraftItemMutationStatusMap = Record<
  number,
  OrderDraftItemMutationEntry | undefined
>

// Overload signature for setItemMutationStatus to better type the function when
// called with InProgressStatus or TerminalStatus.
interface SetItemMutationStatus {
  (itemId: number, status: InProgressStatus): number
  (itemId: number, status: TerminalStatus, seq: number): void
}

interface IOrderDraftItemMutationStatusContextValue {
  getItemMutationStatus: (
    itemId: number
  ) => OrderDraftItemMutationVisibleStatus | null
  setItemMutationStatus: SetItemMutationStatus
  hasInFlightItemMutations: boolean
}

export const OrderDraftItemMutationStatusContext =
  createContext<IOrderDraftItemMutationStatusContextValue | null>(null)

const isInProgressStatus = (
  status: OrderDraftItemMutationStatusEnum
): status is InProgressStatus =>
  status === OrderDraftItemMutationStatuses.CREATING ||
  status === OrderDraftItemMutationStatuses.UPDATING ||
  status === OrderDraftItemMutationStatuses.DELETING

export const OrderDraftItemMutationStatusContextProvider = ({
  children,
}: PropsWithChildren<{}>) => {
  const [mutationStatuses, setMutationStatuses] =
    useState<OrderDraftItemMutationStatusMap>({})

  // Monotonically increasing counter for mutation ordering.
  // Lives in a ref so setItemMutationStatus can return the
  // assigned sequence synchronously.
  const seqCounterRef = useRef(0)

  const getItemMutationStatus = useCallback(
    (itemId: number): OrderDraftItemMutationVisibleStatus | null => {
      return mutationStatuses[itemId]?.status ?? null
    },
    [mutationStatuses]
  )

  const hasInFlightItemMutations = useMemo<boolean>(
    () =>
      Object.values(mutationStatuses).some(
        (entry) => entry !== undefined && isInProgressStatus(entry.status)
      ),
    [mutationStatuses]
  )

  // Terminal writes carry the seq from the originating in-progress call, and
  // any write whose seq is older than the latest is dropped — so stale
  // completions/failures cannot clobber the state of a newer in-flight mutation.
  //
  // useMemo (rather than useCallback) because useCallback's inferred type does
  // not preserve function declaration overloads.
  const setItemMutationStatus = useMemo<SetItemMutationStatus>(() => {
    function impl(itemId: number, status: InProgressStatus): number
    function impl(itemId: number, status: TerminalStatus, seq: number): void
    function impl(
      itemId: number,
      status: OrderDraftItemMutationStatusEnum,
      seq?: number
    ): number | void {
      if (isInProgressStatus(status)) {
        const newSeq = ++seqCounterRef.current

        setMutationStatuses((prev) => ({
          ...prev,
          [itemId]: { status, latestMutationSeq: newSeq },
        }))

        return newSeq
      }

      if (typeof seq !== 'number') {
        throw new TypeError(
          `setItemMutationStatus: terminal status "${status}" requires a seq argument`
        )
      }

      setMutationStatuses((prev) => {
        const current = prev[itemId]

        // Drop stale write if...
        // 1. No active mutation
        if (!current) return prev

        // 2. The write's seq is not the latest
        if (seq < current.latestMutationSeq) return prev

        // Otherwise, update the stored status
        switch (status) {
          case OrderDraftItemMutationStatuses.COMPLETED: {
            const { [itemId]: _, ...rest } = prev
            return rest
          }
          case OrderDraftItemMutationStatuses.FAILED_TO_CREATE:
          case OrderDraftItemMutationStatuses.FAILED_TO_UPDATE:
          case OrderDraftItemMutationStatuses.FAILED_TO_DELETE:
            return {
              ...prev,
              [itemId]: { status, latestMutationSeq: seq },
            }
          default: {
            const _exhaustiveCheck: never = status
            return _exhaustiveCheck
          }
        }
      })
    }

    return impl
  }, [])

  const contextValue = useMemo(
    () => ({
      getItemMutationStatus,
      setItemMutationStatus,
      hasInFlightItemMutations,
      // hasUnsavedChanges, // TODO (PUR-3406): function for checking if any item has inflight or failed statuses indicating there are unsaved changes.
    }),
    [getItemMutationStatus, setItemMutationStatus, hasInFlightItemMutations]
  )

  return (
    <OrderDraftItemMutationStatusContext.Provider value={contextValue}>
      {children}
    </OrderDraftItemMutationStatusContext.Provider>
  )
}
